import { defineStore } from 'pinia';
import { api } from '@/services/http.service';
import { ElNotification, FormInstance } from 'element-plus';
import {
  DEFAULT_GEO_PAYMENT_METHOD_FORM,
  GeoPaymentMethodItem,
  GlobalPaymentMethodItem,
} from '@/models/ps-management/ps-management.model';
import {
  DEFAULT_UNAVAILABLE_HOURS_CONFIG_ITEM,
  DEFAULT_UNAVAILABLE_HOURS_FORM,
  UnavailableHoursConfigItemModel,
  UnavailableHoursSettingsModel,
} from '@/models/ps-management/availability-hours.model';
import { NotificationStatusType } from '@/models/app-common.model';
import { nextTick, ref } from 'vue';
import { isEqual } from 'lodash';
import { showErrorNotification } from '@/utils/show-notification.util';

interface PaymentMethodAvailabilityHoursStore {
  currentPaymentMethod: GeoPaymentMethodItem | GlobalPaymentMethodItem;
  availabilityHoursFormData: UnavailableHoursSettingsModel;
  originalAvailabilityHoursFormData: UnavailableHoursSettingsModel;
  isAvailabilityHoursDataLoading: boolean;
  isAvailabilityHoursSettingsSidebarVisible: boolean;
}

export const unavailableHoursSettingsFormRef = ref<FormInstance>();

export const usePaymentMethodsAvailabilityHoursStore = defineStore({
  id: 'PaymentMethodAvailabilityHoursStore',
  state: (): PaymentMethodAvailabilityHoursStore => ({
    currentPaymentMethod: structuredClone(DEFAULT_GEO_PAYMENT_METHOD_FORM),
    availabilityHoursFormData: structuredClone(DEFAULT_UNAVAILABLE_HOURS_FORM),
    originalAvailabilityHoursFormData: structuredClone(DEFAULT_UNAVAILABLE_HOURS_FORM),
    isAvailabilityHoursDataLoading: false,
    isAvailabilityHoursSettingsSidebarVisible: false,
  }),
  getters: {
    currentPaymentMethodId(state: PaymentMethodAvailabilityHoursStore): string {
      return state.currentPaymentMethod.billingPaymentMethodSettingsId;
    },
    unavailableHoursConfigList(state: PaymentMethodAvailabilityHoursStore): UnavailableHoursConfigItemModel[] {
      return state.availabilityHoursFormData.unavailableHoursConfig;
    },
    isUnavailableSettingsFormChanged(state: PaymentMethodAvailabilityHoursStore): boolean {
      return !isEqual(
        state.availabilityHoursFormData,
        state.originalAvailabilityHoursFormData,
      );
    },
  },
  actions: {
    setAvailabilityHoursLoading(isLoading: boolean): void {
      this.isAvailabilityHoursDataLoading = isLoading;
    },
    setCurrentPaymentMethod(paymentMethod: GeoPaymentMethodItem | GlobalPaymentMethodItem): void {
      this.currentPaymentMethod = paymentMethod;
    },
    setAvailabilityHoursSettingsSidebarVisibility(isVisible: boolean): void {
      this.isAvailabilityHoursSettingsSidebarVisible = isVisible;
    },
    setDefaultPaymentMethod(): void {
      this.currentPaymentMethod = structuredClone(DEFAULT_GEO_PAYMENT_METHOD_FORM);
    },
    removeUnavailableHoursConfigItem(index: number): void {
      const list = this.availabilityHoursFormData.unavailableHoursConfig;

      if (list.length === 1) {
        list[0] = structuredClone(DEFAULT_UNAVAILABLE_HOURS_CONFIG_ITEM);

        nextTick(() => {
          unavailableHoursSettingsFormRef.value?.validateField('unavailableHoursConfig.0.dayList');
          unavailableHoursSettingsFormRef.value?.validateField('unavailableHoursConfig.0.time');
        });

        return;
      }

      list.splice(index, 1);
    },
    addUnavailableHoursConfigItem(): void {
      this.availabilityHoursFormData.unavailableHoursConfig.push(
        structuredClone(DEFAULT_UNAVAILABLE_HOURS_CONFIG_ITEM),
      );
    },
    getTimeRange(item: UnavailableHoursConfigItemModel): [string, string] | number {
      const { from, to } = item.time;

      return from && to ? [from, to] : 0;
    },
    updateTimeRange(
      value: [string, string] | null,
      index: number,
    ): void {
      const list = this.availabilityHoursFormData.unavailableHoursConfig;

      const updatedItem = value
        ? { ...list[index], time: { from: value[0], to: value[1] } }
        : { ...list[index], time: { from: '', to: '' } };

      this.availabilityHoursFormData.unavailableHoursConfig = [
        ...list.slice(0, index),
        updatedItem,
        ...list.slice(index + 1),
      ];
    },
    getPaymentMethodAvailabilityHours(paymentMethodId: string): Promise<UnavailableHoursSettingsModel> {
      if (!paymentMethodId) {
        return Promise.resolve(this.availabilityHoursFormData);
      }

      this.setAvailabilityHoursLoading(true);

      return api.psManagement.getGeoPaymentMethodAvailabilityHoursRequest
        .perform(paymentMethodId)
        .then((availabilityHoursFormData: UnavailableHoursSettingsModel) => {
          const normalizedFormData = structuredClone(availabilityHoursFormData);

          if (!normalizedFormData.unavailableHoursConfig?.length) {
            normalizedFormData.unavailableHoursConfig = [
              structuredClone(DEFAULT_UNAVAILABLE_HOURS_CONFIG_ITEM),
            ];
          }

          this.availabilityHoursFormData = normalizedFormData;
          this.originalAvailabilityHoursFormData = structuredClone(normalizedFormData);

          return normalizedFormData;
        })
        .finally(() => {
          this.setAvailabilityHoursLoading(false);
        });
    },
    updatePaymentMethodAvailabilityHours(): Promise<void> {
      this.setAvailabilityHoursLoading(true);

      return api.psManagement.updateGeoPaymentMethodAvailabilityHoursRequest
        .perform({
          ...this.availabilityHoursFormData,
          id: this.currentPaymentMethodId,
        })
        .then(() => {
          ElNotification({
            title: 'Success',
            message: 'Unavailability hours updated successfully',
            type: NotificationStatusType.SUCCESS,
          });
        })
        .catch(error => {
          showErrorNotification(error);

          throw error;
        })
        .finally(() => {
          this.setAvailabilityHoursLoading(false);
        });
    },
    openAvailabilityHoursSettings(paymentMethod: GeoPaymentMethodItem | GlobalPaymentMethodItem): void {
      this.setCurrentPaymentMethod(paymentMethod);

      this.getPaymentMethodAvailabilityHours(paymentMethod.billingPaymentMethodSettingsId)
        .finally(() => {
          this.setAvailabilityHoursSettingsSidebarVisibility(true);
        });
    },
    closeAvailabilityHoursSettings(): void {
      this.setAvailabilityHoursLoading(false);
      this.setAvailabilityHoursSettingsSidebarVisibility(false);
      this.setDefaultPaymentMethod();
    },
  },
});
